import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Modal from 'react-modal';
import { announcementsApi } from '../../../api/adminApi';
import {
  announcementId,
  announcementBody,
  announcementDateInputValue,
  formatAnnouncementDateShort,
  todayLocalISODate,
  ANNOUNCEMENT_TITLE_MAX_LENGTH,
} from './announcementHelpers';
import './AnnouncementsList.css';

const DEFAULT_LIMIT = 100;
const DELETE_ALL_PHRASE = 'DELETE ALL ANNOUNCEMENTS';

function previewText(body, max = 100) {
  const s = (body || '').replace(/\s+/g, ' ').trim();
  if (s.length <= max) return s || '—';
  return `${s.slice(0, max)}…`;
}

function AnnouncementsList() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(DEFAULT_LIMIT);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [formModalOpen, setFormModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formTitle, setFormTitle] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formAnnouncementDate, setFormAnnouncementDate] = useState('');
  const [formSaving, setFormSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [deleteAllOpen, setDeleteAllOpen] = useState(false);
  const [deleteAllPhrase, setDeleteAllPhrase] = useState('');
  const [deleteAllBusy, setDeleteAllBusy] = useState(false);

  const loadList = (pageNum = page) => {
    setLoading(true);
    setError('');
    announcementsApi
      .list(pageNum, limit)
      .then((res) => {
        setItems(res.list);
        setTotal(res.total);
        setPage(res.page);
      })
      .catch((err) => setError(err.userMessage || 'Failed to load announcements'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadList(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openAddModal = () => {
    setEditingItem(null);
    setFormTitle('');
    setFormDescription('');
    setFormAnnouncementDate(todayLocalISODate());
    setFormError('');
    setFormModalOpen(true);
  };

  const openEditModal = (row) => {
    const aid = announcementId(row);
    if (!aid) return;
    setEditingItem(row);
    setFormTitle((row.title ?? '').slice(0, ANNOUNCEMENT_TITLE_MAX_LENGTH));
    setFormDescription(announcementBody(row));
    setFormAnnouncementDate(announcementDateInputValue(row) || todayLocalISODate());
    setFormError('');
    setFormModalOpen(true);
  };

  const closeFormModal = () => {
    setFormModalOpen(false);
    setEditingItem(null);
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    setFormError('');
    if (!formTitle.trim()) {
      setFormError('Title is required.');
      return;
    }
    if (formTitle.trim().length > ANNOUNCEMENT_TITLE_MAX_LENGTH) {
      setFormError(`Title must be at most ${ANNOUNCEMENT_TITLE_MAX_LENGTH} characters.`);
      return;
    }
    if (!formAnnouncementDate || !/^\d{4}-\d{2}-\d{2}$/.test(formAnnouncementDate)) {
      setFormError('Announcement date is required (use a valid date).');
      return;
    }

    const payload = {
      title: formTitle.trim(),
      description: formDescription,
      announcement_date: formAnnouncementDate,
    };

    if (editingItem) {
      const aid = announcementId(editingItem);
      if (!aid) return;
      const nextTitle = formTitle.trim();
      const nextDesc = formDescription;
      const nextDate = formAnnouncementDate;
      const prevTitle = (editingItem.title ?? '').trim();
      const prevDesc = announcementBody(editingItem);
      const prevDate = announcementDateInputValue(editingItem);
      if (nextTitle === prevTitle && nextDesc === prevDesc && nextDate === prevDate) {
        setFormError('Change the title, description, or date before saving.');
        return;
      }
      setFormSaving(true);
      announcementsApi
        .update(aid, payload)
        .then(() => {
          closeFormModal();
          loadList(page);
        })
        .catch((err) => setFormError(err.userMessage || 'Something went wrong.'))
        .finally(() => setFormSaving(false));
      return;
    }

    setFormSaving(true);
    announcementsApi
      .create(payload)
      .then(() => {
        closeFormModal();
        loadList(page);
      })
      .catch((err) => setFormError(err.userMessage || 'Something went wrong.'))
      .finally(() => setFormSaving(false));
  };

  const handleDeleteClick = (row) => setDeleteTarget(row);
  const handleDeleteCancel = () => setDeleteTarget(null);
  const handleDeleteConfirm = () => {
    const id = announcementId(deleteTarget);
    if (!id) return;
    setDeleting(true);
    announcementsApi
      .delete(id)
      .then(() => {
        setDeleteTarget(null);
        loadList(page);
      })
      .catch((err) => setError(err.userMessage || 'Failed to delete'))
      .finally(() => setDeleting(false));
  };

  const openDeleteAll = () => {
    setDeleteAllPhrase('');
    setDeleteAllOpen(true);
  };

  const closeDeleteAll = () => {
    if (deleteAllBusy) return;
    setDeleteAllOpen(false);
    setDeleteAllPhrase('');
  };

  const handleDeleteAllConfirm = () => {
    if (deleteAllPhrase.trim() !== DELETE_ALL_PHRASE) return;
    setDeleteAllBusy(true);
    setError('');
    announcementsApi
      .deleteAll()
      .then(() => {
        closeDeleteAll();
        loadList(1);
      })
      .catch((err) => {
        setError(err.userMessage || 'Failed to delete all announcements');
        closeDeleteAll();
      })
      .finally(() => setDeleteAllBusy(false));
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));
  const isEditing = editingItem != null;

  return (
    <div className="admin-announcements">
      <div className="admin-announcements-header">
        <h2>Announcements</h2>
        <div className="admin-announcements-header-actions">
          <button
            type="button"
            className="admin-announcements-delete-all"
            onClick={openDeleteAll}
            disabled={loading || items.length === 0}
          >
            Delete all
          </button>
          <button type="button" className="admin-announcements-add" onClick={openAddModal}>
            Add announcement
          </button>
        </div>
      </div>

      {error && (
        <div className="admin-announcements-error" role="alert">
          {error}
        </div>
      )}

      {loading ? (
        <div className="admin-announcements-loading">Loading announcements…</div>
      ) : (
        <div className="admin-announcements-table-wrap">
          <table className="admin-announcements-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Preview</th>
                <th>Announcement date</th>
                <th>Created</th>
                <th className="admin-announcements-actions-col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td colSpan={5} className="admin-announcements-empty">
                    No announcements yet.{' '}
                    <button type="button" className="admin-announcements-empty-btn" onClick={openAddModal}>
                      Add one
                    </button>
                    .
                  </td>
                </tr>
              ) : (
                items.map((row) => {
                  const aid = announcementId(row);
                  const body = announcementBody(row);
                  return (
                    <tr key={aid || row.title}>
                      <td className="admin-announcements-title-cell">{row.title || '—'}</td>
                      <td className="admin-announcements-preview-cell">{previewText(body)}</td>
                      <td className="admin-announcements-date-cell">
                        {formatAnnouncementDateShort(announcementDateInputValue(row))}
                      </td>
                      <td className="admin-announcements-date-cell">
                        {row.created_at
                          ? new Date(row.created_at).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                            })
                          : '—'}
                      </td>
                      <td className="admin-announcements-actions-col">
                        {aid ? (
                          <Link to={`/admin/announcements/${aid}`} className="admin-announcements-btn admin-announcements-btn-view">
                            View
                          </Link>
                        ) : null}
                        <button
                          type="button"
                          className="admin-announcements-btn admin-announcements-btn-edit"
                          onClick={() => openEditModal(row)}
                          disabled={!aid}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="admin-announcements-btn admin-announcements-btn-delete"
                          onClick={() => handleDeleteClick(row)}
                          disabled={!aid}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      {!loading && total > 0 && totalPages > 1 && (
        <div className="admin-announcements-pagination">
          <button
            type="button"
            className="admin-announcements-page-btn"
            onClick={() => loadList(page - 1)}
            disabled={page <= 1}
          >
            Previous
          </button>
          <span className="admin-announcements-page-info">
            Page {page} of {totalPages} ({total} total)
          </span>
          <button
            type="button"
            className="admin-announcements-page-btn"
            onClick={() => loadList(page + 1)}
            disabled={page >= totalPages}
          >
            Next
          </button>
        </div>
      )}

      <Modal
        isOpen={formModalOpen}
        onRequestClose={() => {
          if (!formSaving) closeFormModal();
        }}
        contentLabel={isEditing ? 'Edit announcement' : 'Add announcement'}
        className="admin-announcements-modal-content"
        overlayClassName="admin-announcements-modal-overlay"
      >
        <div className="admin-announcements-modal-inner">
          <h3>{isEditing ? 'Edit announcement' : 'Add announcement'}</h3>
          {isEditing && (
            <p className="admin-announcements-modal-lead">Update the title, description, or announcement date, then save.</p>
          )}
          <form onSubmit={handleFormSubmit}>
            {formError && (
              <div className="admin-announcements-form-error" role="alert">
                {formError}
              </div>
            )}

            <div className="admin-announcements-form-field">
              <div className="admin-announcements-label-row">
                <label htmlFor="ann-form-title">Title *</label>
                <span className="admin-announcements-char-count" aria-live="polite">
                  {formTitle.length}/{ANNOUNCEMENT_TITLE_MAX_LENGTH}
                </span>
              </div>
              <input
                id="ann-form-title"
                type="text"
                value={formTitle}
                onChange={(e) => setFormTitle(e.target.value)}
                placeholder="Short headline"
                disabled={formSaving}
                maxLength={ANNOUNCEMENT_TITLE_MAX_LENGTH}
                aria-describedby="ann-form-title-hint"
              />
              <p id="ann-form-title-hint" className="admin-announcements-field-hint">
                Maximum {ANNOUNCEMENT_TITLE_MAX_LENGTH} characters.
              </p>
            </div>

            <div className="admin-announcements-form-field">
              <label htmlFor="ann-form-description">Description</label>
              <textarea
                id="ann-form-description"
                value={formDescription}
                onChange={(e) => setFormDescription(e.target.value)}
                placeholder="Announcement text…"
                rows={5}
                disabled={formSaving}
              />
            </div>

            <div className="admin-announcements-form-field">
              <label htmlFor="ann-form-date">Announcement date *</label>
              <input
                id="ann-form-date"
                type="date"
                value={formAnnouncementDate}
                onChange={(e) => setFormAnnouncementDate(e.target.value)}
                disabled={formSaving}
              />
            </div>

            <div className="admin-announcements-form-actions">
              <button
                type="button"
                className="admin-announcements-form-cancel"
                onClick={closeFormModal}
                disabled={formSaving}
              >
                Cancel
              </button>
              <button type="submit" className="admin-announcements-form-submit" disabled={formSaving}>
                {formSaving ? (isEditing ? 'Saving…' : 'Creating…') : isEditing ? 'Save changes' : 'Create'}
              </button>
            </div>
          </form>
        </div>
      </Modal>

      {deleteTarget && (
        <div className="admin-announcements-delete-modal" role="dialog" aria-modal="true" aria-labelledby="ann-del-title">
          <div className="admin-announcements-delete-backdrop" onClick={handleDeleteCancel} aria-hidden="true" />
          <div className="admin-announcements-delete-box">
            <h3 id="ann-del-title">Delete announcement?</h3>
            <p>
              This soft-deletes &ldquo;{deleteTarget.title || 'this announcement'}&rdquo;. This cannot be undone from the
              admin UI.
            </p>
            <div className="admin-announcements-delete-actions">
              <button
                type="button"
                className="admin-announcements-btn admin-announcements-btn-cancel"
                onClick={handleDeleteCancel}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="admin-announcements-btn admin-announcements-btn-delete"
                onClick={handleDeleteConfirm}
                disabled={deleting}
              >
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteAllOpen && (
        <div className="admin-announcements-delete-modal" role="dialog" aria-modal="true" aria-labelledby="ann-del-all-title">
          <div className="admin-announcements-delete-backdrop" onClick={closeDeleteAll} aria-hidden="true" />
          <div className="admin-announcements-delete-box admin-announcements-delete-box--wide">
            <h3 id="ann-del-all-title">Delete all announcements?</h3>
            <p>
              This calls <code className="admin-announcements-code">DELETE /admin/announcements</code> and soft-deletes
              every announcement. Type the phrase below exactly to confirm.
            </p>
            <label className="admin-announcements-delete-all-label" htmlFor="ann-delete-all-phrase">
              Type: <strong>{DELETE_ALL_PHRASE}</strong>
            </label>
            <input
              id="ann-delete-all-phrase"
              type="text"
              className="admin-announcements-delete-all-input"
              value={deleteAllPhrase}
              onChange={(e) => setDeleteAllPhrase(e.target.value)}
              autoComplete="off"
              disabled={deleteAllBusy}
            />
            <div className="admin-announcements-delete-actions">
              <button
                type="button"
                className="admin-announcements-btn admin-announcements-btn-cancel"
                onClick={closeDeleteAll}
                disabled={deleteAllBusy}
              >
                Cancel
              </button>
              <button
                type="button"
                className="admin-announcements-btn admin-announcements-btn-delete"
                onClick={handleDeleteAllConfirm}
                disabled={deleteAllBusy || deleteAllPhrase.trim() !== DELETE_ALL_PHRASE}
              >
                {deleteAllBusy ? 'Deleting…' : 'Delete all'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AnnouncementsList;
